// Theme-only, explicit opt-in labels. No network, storage, polling or customer
// identifiers. Do not use today's date as the basis of a paid-order estimate.
export const themeDeliveryEstimate = ({now = new Date(), paidAt = null} = {}) => {
  const basis = new Date(paidAt || now);
  if (!Number.isFinite(basis.getTime())) throw new Error('Invalid delivery date basis.');
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(basis).map(part => [part.type, part.value]));
  const day = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  const format = date => new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  }).format(date);
  const ordered = format(day);
  day.setUTCDate(day.getUTCDate() + 10);
  return {arrival: format(day), basis: `${paidAt ? 'Paid' : 'If ordered'} ${ordered}`,
    timeZone: 'America/New_York', isoDate: day.toISOString().slice(0, 10)};
};

export const applyThemeDeliveryEstimates = (root, now = new Date()) => {
  for (const label of root.querySelectorAll('[data-coozebra-delivery-estimate]')) {
    // Paid pages must explicitly supply their frozen payment timestamp. Never
    // silently substitute the current date for a missing/invalid paid value.
    const isPaid = label.hasAttribute('data-coozebra-delivery-paid-at');
    const paidAt = isPaid ? label.getAttribute('data-coozebra-delivery-paid-at') : null;
    try {
      if (isPaid && !paidAt) throw new Error('Missing paid basis.');
      const eta = themeDeliveryEstimate({now, paidAt});
      const full = label.getAttribute('data-coozebra-delivery-estimate') === 'full';
      label.textContent = full ? `${eta.basis} · Estimated arrival ${eta.arrival}` : `Est. arrival ${eta.arrival}`;
      label.setAttribute('data-coozebra-arrival-date', eta.isoDate);
    } catch {
      label.textContent = isPaid ? 'Check your order confirmation for the delivery estimate.' : 'Estimated delivery: 10 calendar days from payment.';
      label.removeAttribute('data-coozebra-arrival-date');
    }
  }
};

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const refresh = () => applyThemeDeliveryEstimates(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, {once:true});
  else refresh();
  // A tab restored from the back/forward cache or reopened after midnight gets
  // a fresh unpaid estimate, without introducing another timer/worker loop.
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
}
